import React, { useCallback, useRef, useState, useEffect } from "react";
import * as Scrivito from "scrivito";
import { Field, useFormikContext } from "formik";
import { Button } from "components";
import { useConfiguratorSupport, useTranslate } from "hooks";
import { TextInput } from "components/Inputs";
import { Input, Label, Alert } from "reactstrap";
import { Spinner } from "components/Spinner";
import { Icon } from "components/Icon/Icon";
import { IconButton } from "components/IconButton/IconButton";
import { EllipsisWithTooltip } from "components/EllipsisWithTooltipText";
import {
  MAX_SUPPORT_TEXT_AREA_LENGTH,
  MAX_SUPPORT_INPUT_LENGTH,
  MAX_SUPPORT_SUBJECT_LENGTH,
  MAX_ATTACHMENTS,
  MAX_ATTACHMENT_SIZE_MB,
  ALLOWED_EXTENSIONS,
  ALLOWED_MIME_TYPES,
} from "variables";

import {
  validateAttachment,
  validateTotalAttachments,
  SupportInquirySchema,
  type SupportInquiry,
} from "./schema";
import { toast } from "react-toastify";
import type { FormData } from "interfaces/supportform/supportform.types";

interface ConfiguratorSupportBodyProps {
  isDisabled?: boolean;
}

type FileErrors = Record<string, string>;

const isDefinedFile = (file: File | undefined): file is File => Boolean(file);

export const ConfiguratorSupportBody = Scrivito.connect(
  ({ isDisabled }: ConfiguratorSupportBodyProps) => {
    const translate = useTranslate();
    const { handleSendMail, isSubmitting, error, resetState } =
      useConfiguratorSupport();

    const {
      values,
      setFieldValue,
      errors,
      touched,
      setFieldError,
      setFieldTouched,
    } = useFormikContext<SupportInquiry>();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const fileErrorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
      null,
    );
    const [fileErrors, setFileErrors] = useState<FileErrors>({});

    const isFileAllowed = (file: File): boolean => {
      const extention = "." + file.name.split(".").pop()?.toLowerCase();
      return (
        ALLOWED_EXTENSIONS.has(extention || "") &&
        ALLOWED_MIME_TYPES.has(file.type)
      );
    };

    const getBugMessageTemplate = useCallback((): string => {
      return (
        translate.stringOnly("SUPPORT_MESSAGE_TEMPLATE_STEPS") +
        " \n\n\n\n" +
        translate.stringOnly("SUPPORT_MESSAGE_TEMPLATE_CURRENT_BEHAVIOR") +
        " \n\n\n\n" +
        translate.stringOnly("SUPPORT_MESSAGE_TEMPLATE_EXPECTED_BEHAVIOR") +
        " \n\n\n\n"
      );
    }, [translate]);

    useEffect(() => {
      if (values.email && values.email.trim().length > 0) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (emailRegex.test(values.email)) {
          setFieldError("email", undefined);
        }
      } else {
        setFieldError("email", undefined);
      }
      if (values.message && values.message.trim().length >= 5) {
        setFieldError("message", undefined);
      }
      if (values.subject && values.subject.trim().length >= 4) {
        setFieldError("subject", undefined);
      }
    }, [values.email, values.message, values.subject, setFieldError]);

    useEffect(() => {
      if (values.type === "BUG") {
        if (!values.message || values.message === "") {
          setFieldValue("message", getBugMessageTemplate());
        }
      } else if (values.type === "FEEDBACK") {
        const bugTemplate = getBugMessageTemplate();
        if (values.message === bugTemplate) {
          setFieldValue("message", "");
        }
      }
    }, [values.type, setFieldValue, getBugMessageTemplate, values.message]);

    useEffect(
      () => () => {
        if (fileErrorTimeoutRef.current) {
          clearTimeout(fileErrorTimeoutRef.current);
        }
      },
      [],
    );

    const handleFileSelect = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newErrors: FileErrors = {};
        const selectedFiles = Array.from(e.target.files || []).filter(
          (file) => {
            if (!isFileAllowed(file)) {
              const message = translate("FILE_TYPE_NOT_ALLOWED");
              newErrors.general =
                typeof message === "string" ? message : "FILE_TYPE_NOT_ALLOWED";
              return false;
            }
            return true;
          },
        );

        const currentAttachments = (values.attachments || []).filter(
          isDefinedFile,
        );
        const existingNames = currentAttachments.map((f) => f.name);
        const uniqueSelected = selectedFiles.filter((file, idx, arr) => {
          if (existingNames.includes(file.name)) {
            newErrors.general =
              translate("FILE_ALLREADY_ATTACHED") + "  '" + file.name + "'";
            return false;
          }
          return arr.findIndex((f) => f.name === file.name) === idx;
        });

        // Validate that a MAX_ATTACHMENT_SIZE_MB is not exceeded in total uploads
        if (MAX_ATTACHMENTS === Number.POSITIVE_INFINITY) {
          const uploadExceeded = validateTotalAttachments(
            uniqueSelected,
            currentAttachments,
          );
          if (uploadExceeded) {
            newErrors[String(Object.keys(newErrors).length)] = uploadExceeded;
          }
        } else {
          // validate each item to be lower in size as MAX_ATTACHMENT_SIZE_MB
          uniqueSelected.forEach((file, index) => {
            const validationError = validateAttachment(file);
            if (validationError) {
              newErrors[String(index)] = validationError;
            }
          });
        }

        const maxAllowed = MAX_ATTACHMENTS;
        if (currentAttachments.length + uniqueSelected.length > maxAllowed) {
          const message = translate("MAX_ATTACHMENTS_EXCEEDED", {
            max: maxAllowed,
          });
          newErrors.general =
            typeof message === "string"
              ? message
              : `Max ${maxAllowed} files allowed`;
        }

        if (Object.keys(newErrors).length > 0) {
          setFileErrors(newErrors);

          if (fileErrorTimeoutRef.current) {
            clearTimeout(fileErrorTimeoutRef.current);
          }

          fileErrorTimeoutRef.current = setTimeout(() => {
            setFileErrors({});
            fileErrorTimeoutRef.current = null;
          }, 5000);

          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }

          return;
        }

        const remainingSlots = Math.max(
          0,
          maxAllowed - currentAttachments.length,
        );
        const filesToAdd = uniqueSelected.slice(0, remainingSlots);
        const updatedAttachments = [...currentAttachments, ...filesToAdd];
        setFieldValue("attachments", updatedAttachments);
        setFileErrors({});

        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      },
      [values.attachments, setFieldValue, translate],
    );

    const handleRemoveAttachment = useCallback(
      (index: number) => {
        const updatedAttachments = (values.attachments || [])
          .filter(isDefinedFile)
          .filter((_, i) => i !== index);
        setFieldValue("attachments", updatedAttachments);
      },
      [values.attachments, setFieldValue],
    );

    const handleSubmit = useCallback(async () => {
      if (error) {
        toast.error(translate("ERROR") + ": " + (translate(error) || error));
        return;
      }
      const validationResult = SupportInquirySchema.safeParse(values);

      if (!validationResult.success) {
        const validationErrors: Record<string, string> = {};
        validationResult.error.issues.forEach(({ path, message }) => {
          const field = path[0]?.toString();
          if (field && !validationErrors[field]) {
            validationErrors[field] = message;
          }
        });
        Object.entries(validationErrors).forEach(([field, message]) => {
          setFieldTouched(field, true, false);
          setFieldError(field, message);
        });
        return;
      }

      const normalizedValues: FormData = {
        ...values,
        attachments: (values.attachments || []).filter(isDefinedFile),
      };

      const isSuccess = await handleSendMail(normalizedValues);
      if (isSuccess) {
        toast.success(
          translate.stringOnly("SUBMISSION_SUCCESSFUL") +
            " " +
            translate.stringOnly("SUPPORT_TEAM_WILL_CONTACT_YOU"),
        );
        setTimeout(() => {
          setFieldValue("subject", "");
          setFieldValue("attachments", []);
          if (values.type === "BUG") {
            setFieldValue("message", getBugMessageTemplate());
          } else {
            setFieldValue("message", "");
          }
          resetState();
        }, 2000);
      }
    }, [
      error,
      translate,
      values,
      handleSendMail,
      setFieldValue,
      resetState,
      setFieldError,
      setFieldTouched,
      getBugMessageTemplate,
    ]);

    const attachmentsList = (values.attachments || []).filter(isDefinedFile);

    return (
      <div className="mb-2_5">
        <h5 className="mb-4 text-uppercase">{translate("DETAILS")}</h5>
        <div className="d-flex flex-column px-4 py-3 bg-white rounded-2">
          <div className="pt-2_5 d-flex row">
            <div className="col-12 col-lg-6">
              <legend className="d-flex align-items-center mb-3 fs-md text-gray-700 fw-bold">
                {translate("CATEGORISATION")}
              </legend>
              <div className="d-flex gap-4 mb-4">
                <div className="d-flex gap-2_5">
                  <Input
                    name="type"
                    type="radio"
                    data-recording-disable={true}
                    value="BUG"
                    id="TYPE_BUG"
                    disabled={isDisabled || isSubmitting}
                    defaultChecked={values.type === "BUG"}
                    onChange={(e) => setFieldValue("type", e.target.value)}
                  />
                  <Label
                    className="d-flex align-items-center fs-md text-gray-700 cursor-pointer"
                    htmlFor="TYPE_BUG"
                  >
                    {translate("BUG")}
                  </Label>
                </div>
                <div className="d-flex gap-2_5">
                  <Input
                    name="type"
                    type="radio"
                    data-recording-disable={true}
                    value="FEEDBACK"
                    id="TYPE_FEEDBACK"
                    disabled={isDisabled || isSubmitting}
                    onChange={(e) => setFieldValue("type", e.target.value)}
                  />
                  <Label
                    className="d-flex align-items-center fs-md text-gray-700 cursor-pointer"
                    htmlFor="TYPE_FEEDBACK"
                  >
                    {translate("FEEDBACK")}
                  </Label>
                </div>
              </div>
              <Field
                component={TextInput}
                name="email"
                id="email"
                type="email"
                label={translate("SENDER_EMAIL")}
                placeholder="your.email@company.com"
                disabled={isDisabled || isSubmitting}
                tooltipText={translate("SENDER_EMAIL_TOOLTIP")}
                maxLength={MAX_SUPPORT_INPUT_LENGTH}
                error={touched.email && errors.email}
              />
              <Field
                component={TextInput}
                name="subject"
                id="subject"
                type="text"
                label={translate("SUBJECT")}
                placeholder={translate.stringOnly("SUBJECT_PLACEHOLDER")}
                disabled={isDisabled || isSubmitting}
                maxLength={MAX_SUPPORT_SUBJECT_LENGTH}
                error={touched.subject && errors.subject}
              />

              <div className="mt-4">
                <legend className="text-gray-700 fw-bold mb-3 fs-md">
                  {translate("ATTACHMENTS_OPTIONAL")}
                </legend>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.png,.jpg,.jpeg,.txt,.doc,.docx,.xls,.xlsx"
                  onChange={handleFileSelect}
                  disabled={isDisabled || isSubmitting}
                  className="d-none"
                  id="file-upload"
                />
                <Button
                  type="button"
                  color="link"
                  className="gray-300  btn-outline-secondary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isDisabled || isSubmitting}
                >
                  {translate("UPLOAD_DATA")}
                </Button>
                <p className="text-muted fs-sm mt-2">
                  {translate("MAX_FILE_SIZE")} {MAX_ATTACHMENT_SIZE_MB} MB
                </p>

                {Object.keys(fileErrors).length > 0 && (
                  <Alert color="warning" className="mt-3">
                    {Object.entries(fileErrors).map(
                      ([index, attachmentsError]) => (
                        <div key={index} className="fs-sm">
                          {translate(attachmentsError)}
                        </div>
                      ),
                    )}
                  </Alert>
                )}

                {attachmentsList.length > 0 && (
                  <div className="mt-3">
                    <p className="fw-bold text-gray-700 fs-md mb-2">
                      {translate("ATTACHED_FILES")} ({attachmentsList.length})
                    </p>
                    <div className="d-flex flex-column gap-2">
                      {attachmentsList.map((file, index) => (
                        <div
                          key={index}
                          className="d-flex justify-content-between align-items-center gap-2 p-2 border rounded bg-light"
                        >
                          <Icon
                            iconName="file-regular"
                            className="text-secondary"
                          />
                          <span className="w-100 fs-sm text-truncate">
                            <EllipsisWithTooltip
                              text={`${file.name}(${(file.size / 1024).toFixed(2)} KB)`}
                            />
                          </span>

                          <IconButton
                            id={`remove-attachment-${index}`}
                            icon="trash-can-light"
                            iconClassName="icon-16"
                            className="border-0"
                            onClick={() => handleRemoveAttachment(index)}
                            disabled={isSubmitting}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="col-12 col-lg-6 flex-grow-1">
              <Field
                component={TextInput}
                type="textarea"
                name="message"
                id="message"
                placeholder={
                  values.type === "FEEDBACK"
                    ? translate.stringOnly("FEEDBACK_MESSAGE_PLACEHOLDER")
                    : translate("MESSAGE_SUPPORT_PLACEHOLDER")
                }
                label={translate("MESSAGE_REQUIRED")}
                wrapperClassName="h-100 mb-3 pb-2 d-flex flex-column"
                maxLength={MAX_SUPPORT_TEXT_AREA_LENGTH}
                disabled={isDisabled || isSubmitting}
                className="h-100"
                error={touched.message && errors.message}
              />
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end mt-4">
          <Button
            type="button"
            color="primary"
            className="ms-2"
            onClick={handleSubmit}
            disabled={Boolean(
              isDisabled ||
              isSubmitting ||
              !values.message ||
              (touched.message && errors.message) ||
              (touched.subject && errors.subject),
            )}
          >
            {isSubmitting && <Spinner size="xxs" />}
            {isSubmitting
              ? translate.stringOnly("SENDING")
              : translate.stringOnly("SEND")}
          </Button>
        </div>
      </div>
    );
  },
);
